// floorPlanPrint.js

/**
 * Build printable HTML for one floor plan floor (map + item list).
 */

import { readItemRoomKey } from 'nodetypes/project/floorplan/floorPlanItemPlacement';
import {
  DEFAULT_CANVAS_HEIGHT,
  DEFAULT_CANVAS_WIDTH,
} from 'nodetypes/project/floorplan/projectFloorPlanModel';
import { openPrintDocument } from 'utilities/Print';

const PO_ITEM_DATA_KEY = 'data_purchase_order_item';

/**
 * readPrintItemField
 *
 * Reads a typed PO item field without pulling in PO model modules.
 *
 * @param {object|null|undefined} item
 * @param {string} fieldName
 * @returns {unknown}
 */
function readPrintItemField(item, fieldName) {
  const section = item?.[PO_ITEM_DATA_KEY];
  if (section && typeof section === 'object' && fieldName in section) {
    return section[fieldName];
  }
  const alt = item?.data_PURCHASE_ORDER_ITEM || item?.data_Purchase_Order_Item;
  if (alt && typeof alt === 'object' && fieldName in alt) {
    return alt[fieldName];
  }
  return undefined;
}

/**
 * escapeHtml
 *
 * Escapes text for safe HTML insertion.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * buildFloorPlanPrintStyles
 *
 * High-contrast print stylesheet for a single floor sheet.
 *
 * @returns {string}
 */
export function buildFloorPlanPrintStyles() {
  return `
    * {
      box-sizing: border-box;
    }

    html,
    body {
      background: #ffffff;
      color: #000000;
      font-family: Arial, Helvetica, sans-serif;
      margin: 0;
      padding: 0;
    }

    .floor-plan-print-sheet {
      display: flex;
      flex-direction: column;
      gap: 20px;
      margin: 0 auto;
      max-width: 820px;
      padding: 28px;
    }

    .floor-plan-print-header {
      border-bottom: 1px solid #000000;
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding-bottom: 16px;
    }

    .floor-plan-print-caption {
      color: #000000;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .floor-plan-print-title {
      color: #000000;
      font-size: 26px;
      font-weight: 700;
      line-height: 1.2;
      word-break: break-word;
    }

    .floor-plan-print-subtitle {
      color: #000000;
      font-size: 16px;
      font-weight: 600;
    }

    .floor-plan-print-stage-shell {
      border: 1px solid #000000;
      margin: 0 auto;
      max-width: 100%;
      overflow: hidden;
      width: fit-content;
    }

    .floor-plan-print-stage {
      background:
        linear-gradient(#dddddd 1px, transparent 1px) 0 0 / 24px 24px,
        linear-gradient(90deg, #dddddd 1px, transparent 1px) 0 0 / 24px 24px,
        #ffffff;
      position: relative;
    }

    .floor-plan-print-room {
      align-items: center;
      background: #f5f5f5;
      border: 1.5px solid #000000;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      justify-content: center;
      overflow: hidden;
      padding: 4px;
      position: absolute;
    }

    .floor-plan-print-room-name {
      color: #000000;
      font-size: 12px;
      font-weight: 700;
      max-width: 100%;
      overflow: hidden;
      text-align: center;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .floor-plan-print-room-count {
      color: #333333;
      font-size: 10px;
      margin-top: 2px;
      text-align: center;
    }

    .floor-plan-print-section-title {
      border-bottom: 1px solid #000000;
      color: #000000;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.06em;
      padding-bottom: 6px;
      text-transform: uppercase;
    }

    .floor-plan-print-group {
      break-inside: avoid;
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-top: 14px;
    }

    .floor-plan-print-group-label {
      color: #000000;
      font-size: 14px;
      font-weight: 700;
    }

    .floor-plan-print-item {
      border-top: 1px solid #cccccc;
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 6px 0;
    }

    .floor-plan-print-item-title {
      color: #000000;
      font-size: 13px;
      font-weight: 600;
    }

    .floor-plan-print-item-meta {
      color: #333333;
      font-size: 12px;
    }

    .floor-plan-print-empty {
      color: #555555;
      font-size: 12px;
      font-style: italic;
    }

    .floor-plan-print-sheet + .floor-plan-print-sheet {
      break-before: page;
      page-break-before: always;
    }

    @media print {
      .floor-plan-print-sheet {
        max-width: none;
        padding: 0;
      }

      .floor-plan-print-sheet + .floor-plan-print-sheet {
        break-before: page;
        page-break-before: always;
      }
    }
  `;
}

/**
 * buildFloorPlanPrintGroups
 *
 * Groups items by rooms on the given floor (floor order; empty rooms included).
 *
 * @param {object} floor
 * @param {object[]} items
 * @returns {{ roomId: string, roomName: string, items: object[] }[]}
 */
export function buildFloorPlanPrintGroups(floor, items = []) {
  const rooms = Array.isArray(floor?.rooms) ? floor.rooms : [];
  const itemsByRoom = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const roomKey = readItemRoomKey(item);
    if (!roomKey) return;
    if (!itemsByRoom.has(roomKey)) {
      itemsByRoom.set(roomKey, []);
    }
    itemsByRoom.get(roomKey).push(item);
  });

  return rooms.map((room) => ({
    roomId: room.id,
    roomName: room.name || 'Room',
    items: itemsByRoom.get(room.id) || [],
  }));
}

/**
 * formatFloorPlanPrintItemMeta
 *
 * @param {object} item
 * @returns {string}
 */
export function formatFloorPlanPrintItemMeta(item) {
  const quantityRaw = readPrintItemField(item, 'quantity');
  const quantity = quantityRaw == null || quantityRaw === ''
    ? null
    : Number(quantityRaw);
  const parts = [
    Number.isFinite(quantity) ? `Qty ${quantity}` : null,
    String(readPrintItemField(item, 'sku') || '').trim() || null,
    String(readPrintItemField(item, 'manufacturer') || '').trim() || null,
  ].filter(Boolean);
  return parts.join(' · ');
}

/**
 * getFloorPlanPrintItemTitle
 *
 * @param {object} item
 * @returns {string}
 */
export function getFloorPlanPrintItemTitle(item) {
  return String(item?.title || '').trim() || 'Untitled item';
}

/**
 * buildFloorPlanPrintMarkup
 *
 * @param {object} params
 * @param {string} params.projectTitle
 * @param {object} params.floor
 * @param {object[]} [params.items]
 * @param {number} [params.canvasWidth]
 * @param {number} [params.canvasHeight]
 * @returns {string}
 */
export function buildFloorPlanPrintMarkup({
  projectTitle,
  floor,
  items = [],
  canvasWidth = DEFAULT_CANVAS_WIDTH,
  canvasHeight = DEFAULT_CANVAS_HEIGHT,
} = {}) {
  const title = String(projectTitle || '').trim() || 'Project';
  const floorName = String(floor?.name || '').trim() || 'Floor';
  const rooms = Array.isArray(floor?.rooms) ? floor.rooms : [];
  const groups = buildFloorPlanPrintGroups(floor, items);
  const countByRoomId = new Map(
    groups.map((group) => [group.roomId, group.items.length]),
  );

  const roomMarkup = rooms.map((room) => {
    const count = countByRoomId.get(room.id) || 0;
    return `
      <div
        class="floor-plan-print-room"
        style="
          height: ${Number(room.height) || 0}px;
          left: ${Number(room.x) || 0}px;
          top: ${Number(room.y) || 0}px;
          width: ${Number(room.width) || 0}px;
        "
      >
        <div class="floor-plan-print-room-name">${escapeHtml(room.name || 'Room')}</div>
        <div class="floor-plan-print-room-count">
          ${count} item${count === 1 ? '' : 's'}
        </div>
      </div>
    `;
  }).join('');

  const groupsMarkup = groups.length
    ? groups.map((group) => {
      const itemRows = group.items.length
        ? group.items.map((item) => {
          const meta = formatFloorPlanPrintItemMeta(item);
          return `
            <div class="floor-plan-print-item">
              <div class="floor-plan-print-item-title">
                ${escapeHtml(getFloorPlanPrintItemTitle(item))}
              </div>
              ${meta ? `<div class="floor-plan-print-item-meta">${escapeHtml(meta)}</div>` : ''}
            </div>
          `;
        }).join('')
        : '<div class="floor-plan-print-empty">No items</div>';

      return `
        <div class="floor-plan-print-group">
          <div class="floor-plan-print-group-label">
            ${escapeHtml(group.roomName)}
            (${group.items.length})
          </div>
          ${itemRows}
        </div>
      `;
    }).join('')
    : '<div class="floor-plan-print-empty">No rooms on this floor.</div>';

  return `
    <div class="floor-plan-print-sheet">
      <div class="floor-plan-print-header">
        <div class="floor-plan-print-caption">Floor Plan</div>
        <div class="floor-plan-print-title">${escapeHtml(title)}</div>
        <div class="floor-plan-print-subtitle">${escapeHtml(floorName)}</div>
      </div>

      <div class="floor-plan-print-stage-shell">
        <div
          class="floor-plan-print-stage"
          style="height: ${canvasHeight}px; width: ${canvasWidth}px;"
        >
          ${roomMarkup}
        </div>
      </div>

      <div class="floor-plan-print-section-title">Items by room</div>
      ${groupsMarkup}
    </div>
  `;
}

/**
 * buildFloorPlanPrintDocumentMarkup
 *
 * Builds one printable sheet per floor (page break between sheets).
 *
 * @param {object} params
 * @param {string} params.projectTitle
 * @param {object[]} params.floors
 * @param {object[]} [params.items]
 * @param {number} [params.canvasWidth]
 * @param {number} [params.canvasHeight]
 * @returns {string}
 */
export function buildFloorPlanPrintDocumentMarkup({
  projectTitle,
  floors = [],
  items = [],
  canvasWidth = DEFAULT_CANVAS_WIDTH,
  canvasHeight = DEFAULT_CANVAS_HEIGHT,
} = {}) {
  const list = Array.isArray(floors) ? floors.filter((floor) => floor?.id) : [];
  if (!list.length) return '';

  return list.map((floor) => buildFloorPlanPrintMarkup({
    projectTitle,
    floor,
    items,
    canvasWidth,
    canvasHeight,
  })).join('\n');
}

/**
 * printFloorPlan
 *
 * Opens the system print dialog for every floor (each floor on a new page).
 *
 * @param {object} params
 * @param {string} params.projectTitle
 * @param {object[]} params.floors
 * @param {object[]} [params.items]
 * @param {number} [params.canvasWidth]
 * @param {number} [params.canvasHeight]
 * @returns {boolean}
 */
export function printFloorPlan({
  projectTitle,
  floors = [],
  items = [],
  canvasWidth = DEFAULT_CANVAS_WIDTH,
  canvasHeight = DEFAULT_CANVAS_HEIGHT,
} = {}) {
  const list = Array.isArray(floors) ? floors.filter((floor) => floor?.id) : [];
  if (!list.length) return false;

  const project = String(projectTitle || '').trim() || 'Project';
  const title = list.length === 1
    ? `${project} · ${String(list[0].name || '').trim() || 'Floor'}`
    : `${project} · Floor Plan`;

  return openPrintDocument({
    bodyMarkup: buildFloorPlanPrintDocumentMarkup({
      projectTitle,
      floors: list,
      items,
      canvasWidth,
      canvasHeight,
    }),
    styles: buildFloorPlanPrintStyles(),
    title,
  });
}

/**
 * printActiveFloorPlan
 *
 * @deprecated Prefer printFloorPlan (all floors). Kept for call-site compatibility.
 *
 * @param {object} params
 * @param {string} params.projectTitle
 * @param {object} params.floor
 * @param {object[]} [params.items]
 * @param {number} [params.canvasWidth]
 * @param {number} [params.canvasHeight]
 * @returns {boolean}
 */
export function printActiveFloorPlan({
  projectTitle,
  floor,
  items = [],
  canvasWidth = DEFAULT_CANVAS_WIDTH,
  canvasHeight = DEFAULT_CANVAS_HEIGHT,
} = {}) {
  if (!floor?.id) return false;
  return printFloorPlan({
    projectTitle,
    floors: [floor],
    items,
    canvasWidth,
    canvasHeight,
  });
}
